import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import CityDropdown from "./CityDropdown";

const RENT_PRICES = [20000, 30000, 40000, 50000, 60000, 80000, 100000, 150000, 200000, 300000, 500000, 1000000];
const BUY_PRICES = [300000, 500000, 800000, 1000000, 1500000, 2000000, 3000000, 5000000, 8000000, 10000000];

const RESIDENTIAL_TYPES = ["Apartment", "Villa", "Townhouse", "Penthouse", "Hotel Apartment", "Land Residential", "Residential Building"];
const COMMERCIAL_TYPES = ["Office", "Shop", "Showroom", "Warehouse", "Commercial Villa", "Land Commercial", "Commercial Building", "Commercial Floor", "Factory", "Labour Camp", "Industrial Land", "Mixed Use Land"];

const AREA_OPTIONS = [500, 750, 1000, 1500, 2000, 3000, 4000, 5000, 10000];
const ROOM_OPTIONS = ["1", "2", "3", "4", "5+"];
const INITIAL_FORM = {
  propertyCategory: "",
  propertyType: "",
  location: null,
  minPrice: "",
  maxPrice: "",
  minArea: "",
  maxArea: "",
  bedrooms: "",
  bathrooms: "",
};

function formatPrice(price) {
  return `AED ${new Intl.NumberFormat("en-AE", { maximumFractionDigits: 0 }).format(price)}`;
}

function formatArea(area) {
  return `${new Intl.NumberFormat("en-AE", { maximumFractionDigits: 0 }).format(area)} Sqft`;
}

function buildSearchPayload(activeMode, form) {
  return {
    offering_type: activeMode,
    city: form.location?.value || "",
    property_category: form.propertyCategory,
    property_type: form.propertyType,
    bedrooms: form.bedrooms,
    bathrooms: form.bathrooms,
    minPrice: form.minPrice,
    maxPrice: form.maxPrice,
    minArea: form.minArea,
    maxArea: form.maxArea,
    page: "1",
    pageSize: "8",
    sortBy: "id DESC",
  };
}

function appendParams(payload) {
  const params = new URLSearchParams();

  Object.entries(payload).forEach(([key, val]) => {
    if (val !== null && val !== undefined && val !== "") {
      params.set(key, val);
    }
  });

  return params;
}

function PropertySearch() {
  const navigate = useNavigate();
  const [activeMode, setActiveMode] = useState("Buy");
  const [form, setForm] = useState(INITIAL_FORM);

  const prices = activeMode === "Rent" ? RENT_PRICES : BUY_PRICES;
  const unitTypes = useMemo(() => {
    if (form.propertyCategory === "Residential") {
      return RESIDENTIAL_TYPES;
    }

    if (form.propertyCategory === "Commercial") {
      return COMMERCIAL_TYPES;
    }

    return [...RESIDENTIAL_TYPES, ...COMMERCIAL_TYPES];
  }, [form.propertyCategory]);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      minPrice: "",
      maxPrice: "",
    }));
  }, [activeMode]);

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function updateCategory(value) {
    setForm((current) => ({
      ...current,
      propertyCategory: value,
      propertyType: "",
    }));
  }

  function handleModeChange(mode) {
    setActiveMode(mode);
  }

  function handleSubmit(event) {
    event.preventDefault();

    const payload = buildSearchPayload(activeMode, form);
    const params = appendParams(payload);

    window.sessionStorage.setItem("bluewaterPropertySearch", JSON.stringify(payload));
    navigate("/property-search-result?" + params.toString());
  }

  return (
    <div className="search-area">
      <form action="/property-search-result" onSubmit={handleSubmit} autoComplete="off">
        <div className="container mm-0">
          <div className="row justify-content-center">
            <div className="col-xl-10">
              <div className="search-wrap">
                <div className="search-wrap-area-top">
                  <ul className="nav nav-tabs custom" role="tablist">
                    {["Buy", "Rent"].map((mode) => (
                      <li className="nav-item" role="presentation" key={mode}>
                        <button className={`nav-link ${activeMode === mode ? "active" : ""}`} type="button" role="tab" aria-selected={activeMode === mode} onClick={() => handleModeChange(mode)}>
                          {mode}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="active-property-search">
                  <div className="form-group">
                    <label htmlFor="property_category">Property Type</label>
                    <select id="property_category" name="property_category" className="form-select" value={form.propertyCategory} onChange={(event) => updateCategory(event.target.value)}>
                      <option value="">Select Property Type</option>
                      <option value="Residential">Residential</option>
                      <option value="Commercial">Commercial</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="property_type">Unit Type</label>
                    <select id="property_type" name="property_type" className="form-select" value={form.propertyType} onChange={(event) => updateField("propertyType", event.target.value)}>
                      <option value="">Select Unit Type</option>
                      {unitTypes.map((type) => (
                        <option value={type} key={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group city-search-form-group">
                    <label htmlFor="location">Location</label>
                    <CityDropdown value={form.location} onChange={(option) => updateField("location", option)} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="min_price">Min Price</label>
                    <select id="min_price" name="minPrice" className="form-select" value={form.minPrice} onChange={(event) => updateField("minPrice", event.target.value)}>
                      <option value="">Min Price</option>
                      {prices.map((price) => (
                        <option value={price} key={price}>
                          {formatPrice(price)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="max_price">Max Price</label>
                    <select id="max_price" name="maxPrice" className="form-select" value={form.maxPrice} onChange={(event) => updateField("maxPrice", event.target.value)}>
                      <option value="">Max Price</option>
                      {prices.map((price) => (
                        <option value={price} key={price}>
                          {formatPrice(price)}
                        </option>
                      ))}
                    </select>
                  </div>
                   <div className="form-group">
                        <label htmlFor="min_area">Min Area (Sqft)</label>
                        <select id="min_area" name="minArea" className="form-select" value={form.minArea} onChange={(event) => updateField("minArea", event.target.value)}>
                          <option value="">Select Min Area</option>
                          {AREA_OPTIONS.map((area) => (
                            <option value={area} key={area}>
                              {formatArea(area)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="max_area">Max Area (Sqft)</label>
                        <select id="max_area" name="maxArea" className="form-select" value={form.maxArea} onChange={(event) => updateField("maxArea", event.target.value)}>
                          <option value="">Select Max Area</option>
                          {AREA_OPTIONS.map((area) => (
                            <option value={area} key={area}>
                              {formatArea(area)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="bedrooms">Bedrooms</label>
                        <select id="bedrooms" name="bedrooms" className="form-select" value={form.bedrooms} onChange={(event) => updateField("bedrooms", event.target.value)}>
                          <option value="">Select Bedrooms</option>
                          {ROOM_OPTIONS.map((room) => (
                            <option value={room} key={room}>
                              {room === "5+" ? "5+ Bedrooms" : `${room} Bedroom${room === "1" ? "" : "s"}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="bathrooms">Bathrooms</label>
                        <select id="bathrooms" name="bathrooms" className="form-select" value={form.bathrooms} onChange={(event) => updateField("bathrooms", event.target.value)}>
                          <option value="">Select Bathrooms</option>
                          {ROOM_OPTIONS.map((room) => (
                            <option value={room} key={room}>
                              {room === "5+" ? "5+ Bathrooms" : `${room} Bathroom${room === "1" ? "" : "s"}`}
                            </option>
                          ))}
                        </select>
                        
                      </div>
 <div className="form-group">
                <button className="th-btn radius property-search-submit" type="submit">
                      <i className="fa-regular fa-magnifying-glass me-2" />
                      Search Property
                    </button>
</div>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
      <style>{`
        .active-property-search {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          column-gap: 10px;
          row-gap: 14px;
          align-items: end;
        }

        .active-property-search .form-group {
          min-width: 0;
          margin-bottom: 0;
        }

        .active-property-search label {
          display: block;
          color: #fff;
          font-size: 14px;
          line-height: 1.2;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .active-property-search select,
        .city-dropdown-control input {
          width: 100%;
          height: 40px;
          min-height: 40px;
          border: 0;
          border-radius: 8px;
          background: #f0f0f0;
          color: #202020;
          font-size: 13px;
          line-height: 40px;
          padding: 0 12px;
          outline: none;
          box-shadow: none;
        }

        .active-property-search select:focus,
        .city-dropdown-control input:focus {
         background: #f0f0f0;
          box-shadow: 0 0 0 2px rgba(0, 147, 221, 0.35);
        }

        .city-dropdown {
          position: relative;
          width: 100%;
          z-index: 30;
        }

        .city-dropdown-control {
          position: relative;
          width: 100%;
        }

        .city-dropdown-control input {
          padding-right: 54px;
          text-overflow: ellipsis;
        }

        .city-dropdown-clear {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          border: 0;
          background: transparent;
          color: #666;
          font-size: 12px;
          padding: 4px 6px;
        }

        .city-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          max-height: 240px;
          overflow-y: auto;
          background: #fff;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          box-shadow: 0 14px 30px rgba(0, 0, 0, 0.12);
          z-index: 999999;
        }

        .city-dropdown-option {
          display: block;
          width: 100%;
          border: 0;
          background: #fff;
          color: #000;
          text-align: left;
          padding: 10px 14px;
          font-size: 14px;
        }

        .city-dropdown-option:hover,
        .city-dropdown-option:focus {
          background: #f6f6f6;
        }

        .city-dropdown-option--muted {
          color: #777;
          cursor: default;
        }

        .property-search-advanced-grid {
          grid-column: 1 / -1;
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          column-gap: 10px;
          row-gap: 14px;
          align-items: end;
        }

        .property-search-actions {
          grid-column: 1 / -1;
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 10px;
          align-items: end;
        }

        .property-search-actions .th-btn {
          width: 100%;
          height: 42px;
          min-height: 42px;
          border-radius: 4px;
          border: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0 12px;
          white-space: nowrap;
        }

        .property-search-toggle {
          grid-column: 1;
        }

        .property-search-submit {
          grid-column: 2;
        }

        @media (max-width: 1199px) {
          .active-property-search,
          .property-search-advanced-grid,
          .property-search-actions {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .property-search-toggle {
            grid-column: 1;
          }

          .property-search-submit {
            grid-column: 2;
          }
        }

        @media (max-width: 767px) {
          .active-property-search,
          .property-search-advanced-grid,
          .property-search-actions {
           grid-template-columns: repeat(2, minmax(0, 1fr));
           row-gap: 26px;
          }

          .property-search-toggle,
          .property-search-submit {
            grid-column: auto;
          }
        }
      `}</style>
    </div>
  );
}

export default PropertySearch;
